import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import TextField from "@mui/material/TextField";
import MapCard from "./MapCard";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import LockIcon from "@mui/icons-material/Lock";
import ErrorIcon from "@mui/icons-material/Error";
import SettingLobbyComponent from "./SettingLobbyComponent";
import QueueEstimateIndicator from "./QueueEstimateIndicator";
import { TIERS, TIER_LIMITS, TIER_DISPLAY } from "../../constants/membership";
import {
    SCROLLBAR_THUMB_COLOR,
    SCROLLBAR_THUMB_HOVER_COLOR,
    SCROLLBAR_TRACK_COLOR,
} from "../../universal/scrollbarStyles";
import utils from "../../universal/utils";

const RESTRICT_SERVER_LOCATIONS_OPTION = "restrictServerLocationsToArea";

const useStyles = makeStyles((theme) => ({
    lobbySettingsMenu: {
        width: '100%',
        height: '88vh',
        maxHeight: '88vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
    },
    header: {
        background: 'linear-gradient(135deg, #1C1E20 0%, #2A2D30 100%)',
        borderBottom: '2px solid rgba(232, 163, 61, 0.3)',
        padding: '0.66rem 0.85rem 0.62rem',
        position: 'relative',
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(232, 163, 61, 0.5) 50%, transparent 100%)',
        }
    },
    headerTextBlock: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        padding: '0 1.2rem',
        boxSizing: 'border-box',
    },
    headerCloseButton: {
        position: 'absolute',
        top: '0.6rem',
        right: '0.6rem',
        cursor: 'pointer',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '1rem',
        height: '1rem',
        padding: '0.5rem',
        transition: 'background-color 0.3s ease',
        borderRadius: '50%',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        [theme.breakpoints.up("xl")]: {
            width: '1.2rem',
            height: '1.2rem',
            padding: '0.6rem',
            top: '0.75rem',
            right: '0.75rem',
        },
    },
    closeIcon: {
        fontSize: '1.2rem',
        color: '#ffffff',
        fontWeight: 700,
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.35rem',
        },
    },
    headerTitle: {
        color: '#ffffff',
        fontSize: 'clamp(0.95rem, 0.88rem + 0.28vw, 1.32rem)',
        fontWeight: 700,
        marginBottom: '0.22rem',
        textAlign: 'center',
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
        letterSpacing: '0.5px',
    },
    headerSubtitle: {
        color: 'rgba(255, 255, 255, 0.58)',
        fontSize: '0.48rem',
        lineHeight: 1.22,
        textAlign: 'center',
        margin: '0.08rem auto 0',
        maxWidth: '24rem',
        width: 'fit-content',
        display: 'block',
        '&.MuiTypography-root': {
            fontSize: '0.48rem !important',
            lineHeight: '1.22 !important',
            textAlign: 'center !important',
        },
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.54rem',
            '&.MuiTypography-root': {
                fontSize: '0.85rem !important',
            },
        },
    },
    noEditableSettingsContainer: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.35rem',
        marginTop: '0.75rem',
        padding: '0.55rem 0.7rem',
        borderRadius: '999px',
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        [theme.breakpoints.up("xl")]: {
            gap: '0.45rem',
        },
    },
    noEditableSettingsIcon: {
        color: '#A78BFA',
        fontSize: '1rem',
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.1rem',
        },
    },
    noEditableSettings: {
        textAlign: 'center',
        color: '#ffffff',
        fontSize: '0.72rem',
        fontWeight: 500,
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.8rem',
        },
    },
    content: {
        flex: 1,
        minHeight: 0,
        padding: '0.75rem 0.75rem 5.4rem',
        overflow: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin',
        scrollbarColor: `${SCROLLBAR_THUMB_COLOR} ${SCROLLBAR_TRACK_COLOR}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.72rem',
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            background: SCROLLBAR_TRACK_COLOR,
            borderRadius: '999px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: SCROLLBAR_THUMB_COLOR,
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
            borderRadius: '999px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: SCROLLBAR_THUMB_HOVER_COLOR,
        },
        [theme.breakpoints.up("xl")]: {
            padding: '0.9rem 0.9rem 5.7rem',
        },
    },
    floatingQueueEstimate: {
        position: 'absolute',
        left: '50%',
        bottom: '0.78rem',
        transform: 'translateX(-50%)',
        zIndex: 20,
        width: 'min(23rem, calc(100% - 2rem))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.35rem',
        padding: '0.45rem 0.55rem',
        borderRadius: '14px',
        background: 'linear-gradient(180deg, rgba(17, 17, 17, 0.88) 0%, rgba(17, 17, 17, 0.76) 100%)',
        border: '1px solid rgba(232, 163, 61, 0.18)',
        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.38)',
        backdropFilter: 'blur(12px)',
        pointerEvents: 'none',
    },
    floatingQueueTitle: {
        color: '#ffffff',
        fontSize: '0.72rem',
        fontWeight: 700,
        lineHeight: 1,
        textAlign: 'center',
        margin: 0,
    },
    overviewGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: '0.45rem',
    },
    overviewCard: {
        padding: '0.5rem 0.65rem',
        borderRadius: '10px',
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.03) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        minHeight: '3.85rem',
    },
    overviewLabel: {
        color: 'rgba(255, 255, 255, 0.48)',
        fontSize: '0.5rem',
        fontWeight: 600,
        letterSpacing: '0.45px',
        textTransform: 'uppercase',
        marginBottom: '0.16rem',
        '&.MuiTypography-root': {
            fontSize: '1rem !important',
        },
    },
    overviewValue: {
        color: '#ffffff',
        fontSize: '0.66rem',
        fontWeight: 700,
        lineHeight: 1.2,
        marginBottom: '0.14rem',
        '&.MuiTypography-root': {
            fontSize: '0.66rem !important',
        },
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.72rem',
            '&.MuiTypography-root': {
                fontSize: '1.2rem !important',
            },
        },
    },
    overviewHelper: {
        color: 'rgba(255, 255, 255, 0.56)',
        fontSize: '0.46rem',
        lineHeight: 1.2,
        '&.MuiTypography-root': {
            fontSize: '0.8rem !important',
            lineHeight: '1.2 !important',
        },
    },
    sectionCard: {
        position: 'relative',
        padding: '0.9rem',
        borderRadius: '14px',
        background: 'linear-gradient(180deg, rgba(17, 17, 17, 0.92) 0%, rgba(23, 23, 23, 0.9) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.03)',
        [theme.breakpoints.up("xl")]: {
            padding: '1rem',
        },
    },
    sectionHeader: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '0.75rem',
        marginBottom: '0.9rem',
    },
    sectionTextStack: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.2rem',
        minWidth: 0,
    },
    settingsSectionTitle: {
        color: '#ffffff',
        fontSize: '0.96rem',
        fontWeight: 700,
        textAlign: 'left',
        margin: 0,
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.05rem',
        },
    },
    sectionDescription: {
        color: 'rgba(255, 255, 255, 0.56)',
        fontSize: '0.82rem !important',
        lineHeight: 1.5,
        margin: 0,
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.8rem',
        },
    },
    sectionAction: {
        alignSelf: 'center',
        textTransform: 'none',
        borderRadius: '999px',
        padding: '0.3rem 0.8rem',
        color: '#ffffff',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        fontWeight: 500,
        fontSize: '0.72rem',
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: 'rgba(232, 163, 61, 0.12)',
            borderColor: 'rgba(232, 163, 61, 0.3)',
        },
        '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.32)',
            borderColor: 'rgba(255, 255, 255, 0.06)',
        },
    },
    setupGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '0.85rem',
        [theme.breakpoints.up("md")]: {
            gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 1fr)',
        },
    },
    subSectionCard: {
        padding: '0.85rem',
        borderRadius: '12px',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
    },
    subSectionHeader: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '0.75rem',
        marginBottom: '0.85rem',
    },
    subsectionTitle: {
        color: '#ffffff',
        fontSize: '0.82rem',
        fontWeight: 600,
        textAlign: 'left',
        margin: 0,
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.9rem',
        },
    },
    subSectionDescription: {
        color: 'rgba(255, 255, 255, 0.54)',
        fontSize: '0.85rem !important',
        lineHeight: 1.45,
        marginTop: '0.25rem',
    },
    mapGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '0.55rem',
        [theme.breakpoints.up("sm")]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
    },
    listGrid: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.55rem',
    },
    selectionSummary: {
        color: '#8C8F8A',
        fontSize: '0.85rem !important',
        fontWeight: 500,
        marginTop: '1rem !important',
        textTransform: 'uppercase',
        letterSpacing: '0.35px',
    },
    emptyState: {
        color: 'rgba(255, 255, 255, 0.48)',
        fontSize: '0.78rem',
        textAlign: 'center',
        padding: '1rem 0.5rem',
    },
    containerMatchmakingOptionsRows: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.55rem',
    },
    rulesGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '0.85rem',
        [theme.breakpoints.up("md")]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
    },
    ruleGroupCard: {
        padding: '0.85rem',
        borderRadius: '12px',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
    },
    ruleGroupTitle: {
        color: '#ffffff',
        fontSize: '0.82rem',
        fontWeight: 600,
        marginBottom: '0.25rem',
    },
    ruleGroupDescription: {
        color: 'rgba(255, 255, 255, 0.5)',
        fontSize: '0.85rem !important',
        lineHeight: 1.45,
        marginBottom: '0.8rem !important',
    },
}));

import { AVAILABLE_MAPS, AVAILABLE_WINGMAN_MAPS } from "../../constants/maps";

const basePath = utils.getEdgeBasePath();

const settingsConfig = {
    experienceRequirements: [
        {
            id: "setting_01",
            name: "above_hours",
            title: "Minimum playtime required",
            icon: "AccessTime",
            toggleState: false,
            sliderValue: 100,
            showSlider: true,
            sliderConfig: {
                min: 0,
                max: 1000,
                step: 1,
                label: 'hours'
            }
        },
        {
            id: "setting_02",
            name: "total_steam_cs_matches_played",
            title: "Minimum Valve MM matches",
            icon: "SportsEsports",
            toggleState: false,
            sliderValue: 10,
            showSlider: true,
            sliderConfig: {
                min: 0,
                max: 500,
                step: 1,
                label: 'matches'
            }
        },
        {
            id: "setting_03",
            name: "total_faceit_matches_played",
            title: "Minimum FaceIT matches",
            icon: "SportsEsports",
            toggleState: false,
            sliderValue: 10,
            showSlider: true,
            sliderConfig: {
                min: 0,
                max: 500,
                step: 1,
                label: 'matches'
            }
        }
    ],
    skillRestrictions: [
        {
            id: "setting_04",
            name: "maxEloDifferenceInTeam",
            title: "Maximum ELO gap between players",
            icon: "BubbleChart",
            toggleState: false,
            sliderValue: 150,
            showSlider: true,
            sliderConfig: {
                min: 0,
                max: 1000,
                step: 1,
                label: 'ELO Diff'
            }
        },
        {
            id: "setting_05",
            name: "min_player_age",
            title: "Minimum Age Requirement",
            icon: "Person",
            toggleState: false,
            sliderValue: 16,
            showSlider: true,
            sliderConfig: {
                min: 14,
                max: 100,
                step: 1,
                label: 'Age'
            }
        }
    ],
    inventoryChecks: [
        {
            id: "setting_06",
            name: "player_has_inventory",
            title: "Match players with an inventory",
            icon: "LocalMall",
            toggleState: false,
            showSlider: false,
        },
        {
            id: "setting_07",
            name: "player_has_expensive_inventory",
            title: "Minimum Inventory Value ($500+)",
            icon: "AttachMoney",
            toggleState: false,
            showSlider: false,
        }
    ],
    playerFilters: [
        {
            id: "setting_08",
            name: "premium_or_pro_only",
            title: "Premium or Pro players only",
            icon: "WorkspacePremium",
            toggleState: false,
            showSlider: false,
        },
        {
            id: "setting_09",
            name: "biometric_verified_only",
            title: "Biometrically verified players only",
            icon: "Fingerprint",
            toggleState: false,
            showSlider: false,
        },
        {
            id: "setting_10",
            name: "verified_signup_only",
            title: "Basic identity verified players only",
            description: "Only match with players who completed at least Tier 2 onboarding.",
            icon: "VerifiedUser",
            toggleState: false,
            showSlider: false,
        },
        {
            id: "setting_11",
            name: "prefer_female_teammates_if_possible",
            title: "Prioritize playing with other women if possible",
            icon: "Female",
            toggleState: false,
            showSlider: false,
            femaleOnly: true,
        }
    ]
};

const normalizeLocationId = (locationId) => {
    if (locationId === null || locationId === undefined) {
        return "";
    }
    return String(locationId).trim();
};

const formatLocationIdFallback = (locationId) =>
    String(locationId || "")
        .trim()
        .split("-")
        .filter(Boolean)
        .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
        .join(" ");

// Helper function to initialize settings from options
function initializeSettingsFromOptions(options) {
    const updatedSettings = {
        experienceRequirements: settingsConfig.experienceRequirements.map(setting => {
            const isEnabled = options[setting.name] || false;
            const value = options[`${setting.name}_figure`] || setting.sliderValue;
            return {
                ...setting,
                toggleState: isEnabled,
                sliderValue: value
            };
        }),
        skillRestrictions: settingsConfig.skillRestrictions.map(setting => {
            const isEnabled = options[setting.name] || false;
            const value = options[`${setting.name}_figure`] || setting.sliderValue;
            return {
                ...setting,
                toggleState: isEnabled,
                sliderValue: value
            };
        }),
        inventoryChecks: settingsConfig.inventoryChecks.map(setting => {
            const isEnabled = options[setting.name] || false;
            return {
                ...setting,
                toggleState: isEnabled
            };
        }),
        playerFilters: settingsConfig.playerFilters.map(setting => {
            const isEnabled = options[setting.name] || false;
            return {
                ...setting,
                toggleState: isEnabled
            };
        }),
    };
    return updatedSettings;
}

function initializeCountryFilterFromOptions(options) {
    return {
        enabled: options.play_with_countries || false,
        selectedCodes: Array.isArray(options.play_with_countries_list) ? options.play_with_countries_list : [],
    };
}

export default function LobbySettingsMenu({ 
    setMenuSettingsOpen, 
    lobbyMapsSelected,
    selectedServerLocations = [],
    currentUserGender = null,
    completedOnboardingTier = 0,
    onMapClicked, 
    onToggleAllMaps,
    onLeaderMapUpdate,
    onLeaderToggleAllMaps,
    onServerLocationClicked,
    onLeaderServerLocationUpdate,
    onToggleAllServerLocations,
    onLeaderToggleAllServerLocations,
    onMatchmakingOptionChange,
    currentLobbyCode,
    canEditLobbySettings = false,
    matchmakingOptions = {},
    onLeaderMatchmakingUpdate,
    membershipTier = TIERS.FREE,
    serverLocations,
    onSelectionError,
    settingsResetVersion = 0,
    queueEstimate = null,
    queueMode = "standard",
}) {
    const displayLocations = Array.isArray(serverLocations)
        ? serverLocations.map((location, index) => {
            const normalizedLocationId = normalizeLocationId(location?.id ?? location?.awsRegion);
            const displayName =
                location?.displayName ||
                location?.name ||
                formatLocationIdFallback(normalizedLocationId) ||
                "Unknown region";
            const primarySubtitle =
                location?.subtitle ||
                location?.metaDescription ||
                location?.country ||
                "";
            return {
                id: normalizedLocationId,
                uiKey: `${normalizedLocationId || "unknown"}-${index}`,
                name: displayName,
                subtitle: [primarySubtitle, normalizedLocationId].filter(Boolean).join(" • "),
                available: location?.enabled !== false && Boolean(normalizedLocationId),
                flagImageUrl: location?.flagImageUrl || "",
                isInArea: location?.isInArea !== false,
                requiresAreaOptOut: location?.requiresAreaOptOut === true,
                areaGroupName: location?.areaGroupName || "",
            };
        })
        : [];
    const classes = useStyles();
    const [localSettings, setLocalSettings] = useState(() => {
        return initializeSettingsFromOptions(matchmakingOptions);
    });
    const [localCountryFilter, setLocalCountryFilter] = useState(() =>
        initializeCountryFilterFromOptions(matchmakingOptions)
    );
    const [countries, setCountries] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(false);
    const [countriesFetchAttempted, setCountriesFetchAttempted] = useState(false);
    const [countrySearch, setCountrySearch] = useState("");
    const lastLobbyCode = useRef(currentLobbyCode);
    const lastOptionsRef = useRef(matchmakingOptions);
    const lastSettingsResetVersionRef = useRef(settingsResetVersion);

    // Reinitialize whenever lobby code changes OR matchmakingOptions reference changes
    useEffect(() => {
        const lobbyChanged = lastLobbyCode.current !== currentLobbyCode;
        const optionsChanged = lastOptionsRef.current !== matchmakingOptions;
        
        if (lobbyChanged || optionsChanged) {
            const initializedSettings = initializeSettingsFromOptions(matchmakingOptions);
            setLocalSettings(initializedSettings);
            setLocalCountryFilter(initializeCountryFilterFromOptions(matchmakingOptions));
            lastLobbyCode.current = currentLobbyCode;
            lastOptionsRef.current = matchmakingOptions;
        }
    }, [matchmakingOptions, currentLobbyCode]);

    useEffect(() => {
        if (lastSettingsResetVersionRef.current === settingsResetVersion) {
            return;
        }

        setLocalSettings(initializeSettingsFromOptions(matchmakingOptions));
        setLocalCountryFilter(initializeCountryFilterFromOptions(matchmakingOptions));
        lastSettingsResetVersionRef.current = settingsResetVersion;
    }, [settingsResetVersion, matchmakingOptions]);

    // Load countries list once
    useEffect(() => {
        if (countriesFetchAttempted || countriesLoading) return;
        setCountriesFetchAttempted(true);
        setCountriesLoading(true);
        fetch(`${basePath}/api/v1/lobby/countries`)
            .then(r => r.json())
            .then(data => {
                if (data?.ok && Array.isArray(data.countries)) {
                    setCountries(data.countries);
                }
            })
            .catch(() => {})
            .finally(() => setCountriesLoading(false));
    }, [countriesFetchAttempted, countriesLoading]);

    const tierLimits = TIER_LIMITS[membershipTier] || TIER_LIMITS[TIERS.FREE];
    const canRefineSelections = tierLimits.canEditMatchmakingSettings;
    const restrictServerLocationsToArea =
        matchmakingOptions?.[RESTRICT_SERVER_LOCATIONS_OPTION] !== false;
    const canUseFemalePreference = currentUserGender === "female";
    const mapEditingDisabled = (!canEditLobbySettings && currentLobbyCode) || !canRefineSelections;
    const locationEditingDisabled = (!canEditLobbySettings && currentLobbyCode) || !canRefineSelections;
    const mapPool = queueMode === "wingman" ? AVAILABLE_WINGMAN_MAPS : AVAILABLE_MAPS;
    const availableMapsFormatted = mapPool.map(map => map.toLowerCase().replace(/\s+/g, "_"));
    const allMapsSelected = availableMapsFormatted.every(map => lobbyMapsSelected.includes(map));
    const normalizedSelectedLocationIds = selectedServerLocations
        .map((locationId) => normalizeLocationId(locationId))
        .filter(Boolean);
    const selectableServerLocationIds = displayLocations
        .filter((location) => location.available && (!restrictServerLocationsToArea || location.isInArea))
        .map((location) => normalizeLocationId(location.id))
        .filter(Boolean);
    const allServerLocationsSelected =
        selectableServerLocationIds.length > 0 &&
        selectableServerLocationIds.every((locationId) =>
            normalizedSelectedLocationIds.includes(locationId)
        );

    const handleMapClick = (mapName) => {
        if (mapEditingDisabled) {
            return;
        }

        const normalizedMapName = String(mapName || "").trim().toLowerCase().replace(/\s+/g, "_");

        if (canEditLobbySettings && currentLobbyCode && onLeaderMapUpdate) {
            onLeaderMapUpdate(normalizedMapName);
        } else if (onMapClicked) {
            onMapClicked(normalizedMapName);
        }
    };

    const handleToggleAllMaps = () => {
        if (mapEditingDisabled) {
            return;
        }

        const selectAll = !allMapsSelected;
        
        if (canEditLobbySettings && currentLobbyCode && onLeaderToggleAllMaps) {
            onLeaderToggleAllMaps(mapPool, selectAll);
        } else if (onToggleAllMaps) {
            onToggleAllMaps(mapPool, selectAll);
        }
    };

    const handleServerLocationClick = (locationId) => {
        if (locationEditingDisabled) {
            return;
        }

        const normalizedLocationId = normalizeLocationId(locationId);
        const location = displayLocations.find((candidate) => candidate.id === normalizedLocationId);
        if (restrictServerLocationsToArea && location?.isInArea === false) {
            return;
        }

        if (canEditLobbySettings && currentLobbyCode && onLeaderServerLocationUpdate) {
            onLeaderServerLocationUpdate(normalizedLocationId);
        } else if (onServerLocationClicked) {
            onServerLocationClicked(normalizedLocationId);
        }
    };

    const handleAreaRestrictionToggle = () => {
        if (locationEditingDisabled) {
            return;
        }

        const nextRestrictToArea = !restrictServerLocationsToArea;
        const settingData = {
            name: RESTRICT_SERVER_LOCATIONS_OPTION,
            enabled: nextRestrictToArea,
            hasSlider: false,
        };

        if (canEditLobbySettings && currentLobbyCode && onLeaderMatchmakingUpdate) {
            onLeaderMatchmakingUpdate(settingData);
        } else if (onMatchmakingOptionChange) {
            onMatchmakingOptionChange(settingData);
        }
    };

    const handleToggleAllServerLocations = () => {
        if (locationEditingDisabled || selectableServerLocationIds.length === 0) {
            return;
        }

        const selectAll = !allServerLocationsSelected;
        if (
            canEditLobbySettings &&
            currentLobbyCode &&
            onLeaderToggleAllServerLocations
        ) {
            onLeaderToggleAllServerLocations(selectableServerLocationIds, selectAll);
        } else if (onToggleAllServerLocations) {
            onToggleAllServerLocations(selectableServerLocationIds, selectAll);
        }
    };

    const isMatchmakingEditingDisabled = membershipTier === TIERS.FREE || (!canEditLobbySettings && currentLobbyCode);

    const handleToggleChange = (settingId, newToggleState) => {
        const allSettings = [
            ...localSettings.experienceRequirements,
            ...localSettings.skillRestrictions,
            ...localSettings.inventoryChecks,
            ...localSettings.playerFilters,
        ];
        const currentSetting = allSettings.find(s => s.id === settingId);
        
        const updatedSettings = {
            experienceRequirements: localSettings.experienceRequirements.map(setting => 
                setting.id === settingId 
                    ? { ...setting, toggleState: newToggleState }
                    : setting
            ),
            skillRestrictions: localSettings.skillRestrictions.map(setting => 
                setting.id === settingId 
                    ? { ...setting, toggleState: newToggleState }
                    : setting
            ),
            inventoryChecks: localSettings.inventoryChecks.map(setting => 
                setting.id === settingId 
                    ? { ...setting, toggleState: newToggleState }
                    : setting
            ),
            playerFilters: localSettings.playerFilters.map(setting => 
                setting.id === settingId 
                    ? { ...setting, toggleState: newToggleState }
                    : setting
            ),
        };
        setLocalSettings(updatedSettings);
        
        if (currentSetting) {
            const settingData = {
                name: currentSetting.name,
                enabled: newToggleState,
                value: currentSetting.showSlider ? currentSetting.sliderValue : undefined,
                hasSlider: currentSetting.showSlider
            };

            // If user is leader in a lobby, update lobby settings
            // Otherwise, update player settings
            if (canEditLobbySettings && currentLobbyCode && onLeaderMatchmakingUpdate) {
                onLeaderMatchmakingUpdate(settingData);
            } else if (onMatchmakingOptionChange) {
                onMatchmakingOptionChange(settingData);
            }
        }
    };

    const handleSliderChange = (settingId, newSliderValue) => {
        const updatedSettings = {
            experienceRequirements: localSettings.experienceRequirements.map(setting => 
                setting.id === settingId 
                    ? { ...setting, sliderValue: newSliderValue }
                    : setting
            ),
            skillRestrictions: localSettings.skillRestrictions.map(setting => 
                setting.id === settingId 
                    ? { ...setting, sliderValue: newSliderValue }
                    : setting
            ),
            inventoryChecks: localSettings.inventoryChecks.map(setting => 
                setting.id === settingId 
                    ? { ...setting, sliderValue: newSliderValue }
                    : setting
            ),
            playerFilters: localSettings.playerFilters.map(setting => 
                setting.id === settingId 
                    ? { ...setting, sliderValue: newSliderValue }
                    : setting
            ),
        };
        setLocalSettings(updatedSettings);
        
        const updatedSetting = [
            ...updatedSettings.experienceRequirements,
            ...updatedSettings.skillRestrictions,
            ...updatedSettings.inventoryChecks
        ].find(s => s.id === settingId);
        
        if (updatedSetting) {
            const settingData = {
                name: updatedSetting.name,
                enabled: updatedSetting.toggleState,
                value: newSliderValue,
                hasSlider: true
            };

            // If user is leader in a lobby, update lobby settings
            // Otherwise, update player settings
            if (canEditLobbySettings && currentLobbyCode && onLeaderMatchmakingUpdate) {
                onLeaderMatchmakingUpdate(settingData);
            } else if (onMatchmakingOptionChange) {
                onMatchmakingOptionChange(settingData);
            }
        }
    };

    const dispatchCountryFilterUpdate = (enabled, selectedCodes) => {
        const settingData = {
            name: "play_with_countries",
            enabled,
            hasSlider: false,
            listValue: selectedCodes,
        };
        if (canEditLobbySettings && currentLobbyCode && onLeaderMatchmakingUpdate) {
            onLeaderMatchmakingUpdate(settingData);
        } else if (onMatchmakingOptionChange) {
            onMatchmakingOptionChange(settingData);
        }
    };

    const handleCountryFilterToggle = () => {
        if (isMatchmakingEditingDisabled) return;
        const newEnabled = !localCountryFilter.enabled;
        setLocalCountryFilter(prev => ({ ...prev, enabled: newEnabled }));
        dispatchCountryFilterUpdate(newEnabled, localCountryFilter.selectedCodes);
    };

    const handleCountryCardToggle = (code2) => {
        if (isMatchmakingEditingDisabled) return;
        const isSelected = localCountryFilter.selectedCodes.includes(code2);
        const newCodes = isSelected
            ? localCountryFilter.selectedCodes.filter(c => c !== code2)
            : [...localCountryFilter.selectedCodes, code2];
        setLocalCountryFilter(prev => ({ ...prev, selectedCodes: newCodes }));
        dispatchCountryFilterUpdate(localCountryFilter.enabled, newCodes);
    };

    const handleToggleAllCountries = () => {
        if (isMatchmakingEditingDisabled) return;
        if (selectableCountryCodes.length === 0) return;

        const nextSelectedCodes = hasCountriesSelected ? [] : selectableCountryCodes;

        setLocalCountryFilter(prev => ({ ...prev, selectedCodes: nextSelectedCodes }));
        dispatchCountryFilterUpdate(localCountryFilter.enabled, nextSelectedCodes);
    };

    const filteredCountries = countrySearch.trim()
        ? countries.filter(c =>
            c.displayName.toLowerCase().includes(countrySearch.toLowerCase()) ||
            (c.code2 || "").toLowerCase().includes(countrySearch.toLowerCase())
        )
        : countries;
    const selectableCountryCodes = countries
        .map((country) => String(country.code2 || "").trim())
        .filter(Boolean);
    const hasCountriesSelected = localCountryFilter.selectedCodes.length > 0;
    const [verifiedSignupNudgeDismissed, setVerifiedSignupNudgeDismissed] = useState(() => (
        window.localStorage.getItem("ouro_verified_signup_only_nudge_dismissed") === "true"
    ));
    const verifiedSignupEnabled = Boolean(matchmakingOptions?.verified_signup_only);
    const showVerifiedSignupNudge =
        Number(completedOnboardingTier || 0) >= 2 &&
        !isMatchmakingEditingDisabled &&
        !verifiedSignupEnabled &&
        !verifiedSignupNudgeDismissed;
    const dismissVerifiedSignupNudge = () => {
        window.localStorage.setItem("ouro_verified_signup_only_nudge_dismissed", "true");
        setVerifiedSignupNudgeDismissed(true);
    };
    const enableVerifiedSignupOnly = () => {
        if (isMatchmakingEditingDisabled) return;
        const setting = localSettings.playerFilters.find((candidate) => candidate.name === "verified_signup_only");
        if (setting) {
            handleToggleChange(setting.id, true);
        }
        dismissVerifiedSignupNudge();
    };
    const tierDisplay = TIER_DISPLAY[membershipTier] || TIER_DISPLAY[TIERS.FREE];
    const overviewItems = [
        {
            label: "Access",
            value: currentLobbyCode
                ? canEditLobbySettings
                    ? "Leader controls"
                    : "Read only"
                : "Your defaults",
            helper: currentLobbyCode
                ? canEditLobbySettings
                    ? "Changes apply to everyone in this lobby."
                    : "Only the leader can save shared lobby changes."
                : "These preferences apply when you create or join queue.",
        },
        {
            label: "Maps",
            value: `${lobbyMapsSelected.length}/${mapPool.length}`,
            helper: !canRefineSelections
                ? "Free keeps every map active."
                : "Refine the pool before you queue.",
        },
        {
            label: "Regions",
            value: displayLocations.length > 0
                ? `${normalizedSelectedLocationIds.length}/${displayLocations.length}`
                : "0",
            helper: !canRefineSelections
                ? "Free keeps enabled locations active."
                : "Choose where matches may be hosted.",
        },
        {
            label: "Plan",
            value: tierDisplay.name,
            helper: canRefineSelections
                ? "Advanced matchmaking filters are unlocked below."
                : "Upgrade to customize matchmaking preferences.",
            valueColor: tierDisplay.color,
        },
    ];
    const matchmakingGroups = [
        {
            key: "experienceRequirements",
            title: "Experience requirements",
            description: "Filter by playtime and platform match history.",
            settings: localSettings.experienceRequirements,
        },
        {
            key: "skillRestrictions",
            title: "Skill restrictions",
            description: "Keep teams closer in age and rating range.",
            settings: localSettings.skillRestrictions,
        },
        {
            key: "inventoryChecks",
            title: "Inventory checks",
            description: "Prefer players with an inventory signal.",
            settings: localSettings.inventoryChecks,
        },
        {
            key: "playerFilters",
            title: "Player quality",
            description: "Limit the queue to players who gave Ouro stronger trust signals.",
            settings: localSettings.playerFilters.filter(
                (setting) => !setting.femaleOnly || canUseFemalePreference
            ),
        },
    ].filter((group) => group.settings.length > 0);

    return (
        <div className={classes.lobbySettingsMenu}>
            <div className={classes.header}>
                <div className={classes.headerCloseButton} onClick={() => setMenuSettingsOpen(false)}>
                    <CloseIcon className={classes.closeIcon} />
                </div>
                <div className={classes.headerTextBlock}>
                    <Typography className={classes.headerTitle}>Lobby Settings</Typography>
                    <Typography
                        className={classes.headerSubtitle}
                        style={{
                            fontSize: "0.46rem",
                            lineHeight: 1.2,
                            textAlign: "center",
                            maxWidth: "22rem",
                            margin: "0.04rem auto 0",
                            display: "block",
                        }}
                    >
                        Maps and server locations sit side by side up top, with advanced filters grouped into smaller cards below.
                    </Typography>
                </div>
                {!canEditLobbySettings && currentLobbyCode && (
                    <div className={classes.noEditableSettingsContainer}>
                        <ErrorIcon className={classes.noEditableSettingsIcon} />
                        <Typography className={classes.noEditableSettings}>Only the Leader can change the lobby settings</Typography>
                    </div>
                )}
            </div>
            
            <div
                className={classes.content}
                data-lenis-prevent
                data-lenis-prevent-wheel
                data-lenis-prevent-touch
            >
                <div className={classes.overviewGrid}>
                    {overviewItems.map((item) => (
                        <div key={item.label} className={classes.overviewCard}>
                            <Typography className={classes.overviewLabel} style={{ fontSize: "0.48rem" }}>
                                {item.label}
                            </Typography>
                            <Typography
                                className={classes.overviewValue}
                                style={{
                                    fontSize: "0.62rem",
                                    marginBottom: "0.1rem",
                                    ...(item.valueColor ? { color: item.valueColor } : {}),
                                }}
                            >
                                {item.value}
                            </Typography>
                            <Typography
                                className={classes.overviewHelper}
                                style={{ fontSize: "0.43rem", lineHeight: 1.15 }}
                            >
                                {item.helper}
                            </Typography>
                        </div>
                    ))}
                </div>

                <div className={classes.sectionCard}>
                    <div className={classes.sectionHeader}>
                        <div className={classes.sectionTextStack}>
                            <Typography className={classes.settingsSectionTitle}>Match Setup</Typography>
                            <Typography className={classes.sectionDescription}>
                                Use the modal width for the two core match decisions first: which maps can come up and where the game may be hosted.
                            </Typography>
                        </div>
                    </div>

                    <div className={classes.setupGrid}>
                        <div className={classes.subSectionCard}>
                            <div className={classes.subSectionHeader}>
                                <div className={classes.sectionTextStack}>
                                    <Typography className={classes.subsectionTitle}>Maps</Typography>
                                    <Typography className={classes.subSectionDescription}>
                                        {!canRefineSelections
                                            ? "Free keeps all maps active. Upgrade to Premium or Pro to refine this list."
                                            : "Select the exact map pool you want in rotation."}
                                    </Typography>
                                </div>
                                <Button
                                    className={classes.sectionAction}
                                    onClick={handleToggleAllMaps}
                                    disabled={mapEditingDisabled}
                                >
                                    {allMapsSelected ? "Clear maps" : "Select all"}
                                </Button>
                            </div>

                            <div className={classes.mapGrid}>
                                {mapPool.map((mapName) => (
                                    <MapCard
                                        key={mapName}
                                        name={mapName}
                                        mapsSelected={lobbyMapsSelected}
                                        onMapClicked={handleMapClick}
                                        disabled={mapEditingDisabled}
                                    />
                                ))}
                            </div>

                            <Typography className={classes.selectionSummary}>
                                {lobbyMapsSelected.length}/{mapPool.length} maps selected
                            </Typography>
                        </div>

                        <div className={classes.subSectionCard}>
                            <div className={classes.subSectionHeader}>
                                <div className={classes.sectionTextStack}>
                                    <Typography className={classes.subsectionTitle}>Server Locations</Typography>
                                    <Typography className={classes.subSectionDescription}>
                                        {!canRefineSelections
                                            ? "Free keeps all enabled server locations active."
                                            : "Choose the regions your lobby is willing to play on. Far regions may have higher ping."}
                                    </Typography>
                                </div>
                                <Button
                                    className={classes.sectionAction}
                                    onClick={handleToggleAllServerLocations}
                                    disabled={locationEditingDisabled || selectableServerLocationIds.length === 0}
                                >
                                    {allServerLocationsSelected ? "Clear regions" : "Select all"}
                                </Button>
                            </div>

                            <Box
                                component="label"
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                    padding: "0.55rem 0.65rem",
                                    marginBottom: "0.65rem",
                                    borderRadius: "10px",
                                    border: "1px solid rgba(255, 255, 255, 0.1)",
                                    backgroundColor: "rgba(255, 255, 255, 0.03)",
                                    cursor: locationEditingDisabled ? "default" : "pointer",
                                    opacity: canRefineSelections ? 1 : 0.45,
                                }}
                            >
                                <Checkbox
                                    checked={restrictServerLocationsToArea}
                                    onChange={handleAreaRestrictionToggle}
                                    disabled={locationEditingDisabled}
                                    inputProps={{ "aria-label": "Only show nearby regions" }}
                                    checkedIcon={<CheckIcon sx={{ fontSize: "0.85rem", color: "#fff" }} />}
                                    icon={<Box />}
                                    sx={{
                                        width: 18,
                                        height: 18,
                                        padding: 0,
                                        borderRadius: "4px",
                                        border: "1px solid rgba(255, 255, 255, 0.2)",
                                        backgroundColor: restrictServerLocationsToArea
                                            ? "rgba(232, 163, 61, 0.9)"
                                            : "rgba(255, 255, 255, 0.06)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        mt: "0.05rem",
                                        color: "#fff",
                                        "&.Mui-disabled": {
                                            color: "rgba(255, 255, 255, 0.35)",
                                        },
                                    }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ color: "#fff", fontSize: "0.78rem", fontWeight: 700 }}>
                                        Only show nearby regions
                                    </Typography>
                                    <Typography sx={{ color: "rgba(255, 255, 255, 0.48)", fontSize: "0.67rem", lineHeight: 1.3 }}>
                                        On by default. Premium and Pro can uncheck this to queue on any global server location.
                                    </Typography>
                                </Box>
                            </Box>

                            <div className={classes.listGrid}>
                                {displayLocations.length === 0 && (
                                    <Typography className={classes.emptyState}>
                                        No enabled server locations are available yet.
                                    </Typography>
                                )}
                                {displayLocations.map((location) => {
                                    const normalizedLocationId = normalizeLocationId(location.id);
                                    const isAreaLocked = restrictServerLocationsToArea && !location.isInArea;
                                    const isAvailable = location.available && !isAreaLocked;
                                    const isSelected = normalizedSelectedLocationIds.includes(normalizedLocationId);
                                    const isClickable = isAvailable && !locationEditingDisabled;
                                    return (
                                        <Box
                                            key={location.uiKey}
                                            onClick={isClickable ? () => handleServerLocationClick(location.id) : undefined}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                padding: "0.65rem 0.75rem",
                                                borderRadius: "10px",
                                                border: isSelected
                                                    ? "1px solid rgba(232, 163, 61, 0.45)"
                                                    : isAvailable
                                                    ? "1px solid rgba(255, 255, 255, 0.1)"
                                                    : "1px solid rgba(255, 255, 255, 0.06)",
                                                opacity: isAvailable ? 1 : 0.48,
                                                backgroundColor: isSelected
                                                    ? "rgba(232, 163, 61, 0.12)"
                                                    : isAreaLocked
                                                    ? "rgba(255, 255, 255, 0.02)"
                                                    : "rgba(255, 255, 255, 0.03)",
                                                cursor: isClickable ? "pointer" : "default",
                                                transition: "border-color 0.15s ease-in-out, background-color 0.15s ease-in-out",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                                                {location.flagImageUrl && (
                                                    <Box
                                                        component="img"
                                                        src={location.flagImageUrl}
                                                        alt={`${location.name} flag`}
                                                        sx={{
                                                            width: 28,
                                                            height: 20,
                                                            borderRadius: "4px",
                                                            objectFit: "cover",
                                                            border: "1px solid rgba(255, 255, 255, 0.12)",
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                )}
                                                <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                                    <Typography sx={{ color: "#fff", fontSize: "0.83rem", fontWeight: 600 }}>
                                                        {location.name}
                                                    </Typography>
                                                    {location.subtitle && (
                                                        <Typography
                                                            sx={{
                                                                color: "rgba(255, 255, 255, 0.42)",
                                                                fontSize: "0.69rem",
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {isAreaLocked
                                                                ? `${location.subtitle} • outside your area`
                                                                : location.subtitle}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                            {isAvailable && (
                                                <Box
                                                    sx={{
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: "4px",
                                                        border: "1px solid rgba(255, 255, 255, 0.2)",
                                                        backgroundColor: isSelected ? "rgba(232, 163, 61, 0.9)" : "rgba(255, 255, 255, 0.06)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        flexShrink: 0,
                                                        ml: 1,
                                                    }}
                                                >
                                                    {isSelected && <CheckIcon sx={{ fontSize: "0.85rem", color: "#fff" }} />}
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </div>

                            {displayLocations.length > 0 && (
                                <Typography className={classes.selectionSummary}>
                                    {normalizedSelectedLocationIds.length}/{displayLocations.length} regions selected
                                </Typography>
                            )}
                        </div>
                    </div>
                </div>

                <div className={classes.sectionCard}>
                    {membershipTier === TIERS.FREE && (
                        <Box sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            borderRadius: "8px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                            padding: "1.5rem",
                            textAlign: "center",
                        }}>
                            <LockIcon sx={{ fontSize: "2rem", color: "#E8A33D", mb: 1 }} />
                            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", mb: 0.5 }}>
                                Premium Feature
                            </Typography>
                            <Typography sx={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "0.8rem", mb: 1.5 }}>
                                Upgrade to Premium to customize matchmaking settings
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                href="/membership/plans"
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    background: "linear-gradient(135deg, #E8A33D 0%, #E8A33D 100%)",
                                    "&:hover": { background: "linear-gradient(135deg, #E66A00 0%, #E68500 100%)" },
                                }}
                            >
                                Upgrade to Premium
                            </Button>
                        </Box>
                    )}
                    <div className={classes.sectionHeader}>
                        <div className={classes.sectionTextStack}>
                            <Typography className={classes.settingsSectionTitle}>Matchmaking Preferences</Typography>
                            <Typography className={classes.sectionDescription}>
                                Advanced filters are broken into smaller cards so you can scan by intent instead of reading one long stack of toggles.
                            </Typography>
                        </div>
                    </div>
                    <div className={classes.rulesGrid}>
                        {showVerifiedSignupNudge && (
                            <Box
                                sx={{
                                    gridColumn: "1 / -1",
                                    p: 1.5,
                                    borderRadius: "10px",
                                    border: "1px solid rgba(232, 163, 61, 0.25)",
                                    backgroundColor: "rgba(232, 163, 61, 0.08)",
                                }}
                            >
                                <Typography sx={{ color: "#fff", fontSize: "0.82rem", fontWeight: 700, mb: 0.5 }}>
                                    You completed basic onboarding
                                </Typography>
                                <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: "0.72rem", mb: 1 }}>
                                    You can choose to only match with players who gave Ouro similar identity signals.
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={enableVerifiedSignupOnly}
                                        disabled={isMatchmakingEditingDisabled}
                                    >
                                        Enable verified-only
                                    </Button>
                                    <Button size="small" onClick={dismissVerifiedSignupNudge}>
                                        Maybe later
                                    </Button>
                                </Box>
                            </Box>
                        )}
                        {matchmakingGroups.map((group) => (
                            <div key={group.key} className={classes.ruleGroupCard}>
                                <Typography className={classes.ruleGroupTitle}>{group.title}</Typography>
                                <Typography className={classes.ruleGroupDescription}>{group.description}</Typography>
                                <div className={classes.containerMatchmakingOptionsRows}>
                                    {group.settings.map((setting) => (
                                        <SettingLobbyComponent
                                            key={setting.id}
                                            setting={setting}
                                            onToggleChange={handleToggleChange}
                                            onSliderChange={handleSliderChange}
                                            showSlider={setting.showSlider}
                                            disabled={isMatchmakingEditingDisabled}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <Box className={classes.subSectionCard} sx={{ mt: 1 }}>
                        <Box className={classes.subSectionHeader}>
                            <Box className={classes.sectionTextStack}>
                                <Typography className={classes.subsectionTitle}>Country Filter</Typography>
                                <Typography className={classes.subSectionDescription}>
                                    {localCountryFilter.enabled
                                        ? localCountryFilter.selectedCodes.length > 0
                                            ? `Matching only with players from ${localCountryFilter.selectedCodes.length} selected countr${localCountryFilter.selectedCodes.length === 1 ? "y" : "ies"}.`
                                            : "Pick the countries you want to stay within."
                                        : "Turn this on if you want matchmaking limited to specific countries."}
                                </Typography>
                            </Box>
                            <Box
                                onClick={handleCountryFilterToggle}
                                sx={{
                                    position: "relative",
                                    width: "2.6rem",
                                    height: "1.4rem",
                                    borderRadius: "20px",
                                    backgroundColor: localCountryFilter.enabled ? "#E8A33D" : "#393939",
                                    cursor: isMatchmakingEditingDisabled ? "default" : "pointer",
                                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                    boxShadow: localCountryFilter.enabled ? "0 4px 15px rgba(232, 163, 61, 0.3)" : "none",
                                    flexShrink: 0,
                                    mt: 0.2,
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        width: "1.1rem",
                                        height: "1.1rem",
                                        top: "0.16rem",
                                        left: localCountryFilter.enabled ? "calc(100% - 1.26rem)" : "0.16rem",
                                        borderRadius: "50%",
                                        backgroundColor: localCountryFilter.enabled ? "#393939" : "#E8A33D",
                                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                    }}
                                />
                            </Box>
                        </Box>

                        {localCountryFilter.enabled && (
                            <Box>
                                <Box sx={{ display: "flex", gap: 0.8, mb: 0.85, alignItems: "center", flexWrap: "wrap" }}>
                                    <TextField
                                        size="small"
                                        placeholder="Search countries..."
                                        value={countrySearch}
                                        onChange={e => setCountrySearch(e.target.value)}
                                        disabled={isMatchmakingEditingDisabled}
                                        sx={{
                                            flex: 1,
                                            minWidth: 180,
                                            "& .MuiOutlinedInput-root": {
                                                color: "#fff",
                                                fontSize: "0.72rem",
                                                backgroundColor: "rgba(255, 255, 255, 0.05)",
                                                borderRadius: "8px",
                                                "& fieldset": { borderColor: "rgba(232, 163, 61, 0.2)" },
                                                "&:hover fieldset": { borderColor: "rgba(232, 163, 61, 0.4)" },
                                                "&.Mui-focused fieldset": { borderColor: "#E8A33D", borderWidth: "1px" },
                                            },
                                            "& .MuiOutlinedInput-input": {
                                                padding: "0.42rem 0.65rem",
                                                "&::placeholder": { color: "rgba(255,255,255,0.35)" },
                                            },
                                        }}
                                    />
                                    <Button
                                        className={classes.sectionAction}
                                        onClick={handleToggleAllCountries}
                                        disabled={isMatchmakingEditingDisabled || selectableCountryCodes.length === 0}
                                    >
                                        {hasCountriesSelected ? "Clear countries" : "Select all"}
                                    </Button>
                                </Box>

                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                                        gap: "0.45rem",
                                    }}
                                >
                                    {countriesLoading && (
                                        <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 2 }}>
                                            <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem" }}>
                                                Loading countries...
                                            </Typography>
                                        </Box>
                                    )}
                                    {!countriesLoading && filteredCountries.length === 0 && (
                                        <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 2 }}>
                                            <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem" }}>
                                                No countries found
                                            </Typography>
                                        </Box>
                                    )}
                                    {!countriesLoading && filteredCountries.map(country => {
                                        const isSelected = localCountryFilter.selectedCodes.includes(country.code2);
                                        return (
                                            <Box
                                                key={country.code2}
                                                onClick={!isMatchmakingEditingDisabled ? () => handleCountryCardToggle(country.code2) : undefined}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "0.45rem",
                                                    padding: "0.48rem 0.55rem",
                                                    borderRadius: "8px",
                                                    border: isSelected
                                                        ? "1px solid rgba(232, 163, 61, 0.45)"
                                                        : "1px solid rgba(255, 255, 255, 0.08)",
                                                    backgroundColor: isSelected
                                                        ? "rgba(232, 163, 61, 0.1)"
                                                        : "rgba(255, 255, 255, 0.03)",
                                                    cursor: !isMatchmakingEditingDisabled ? "pointer" : "default",
                                                    transition: "border-color 0.15s ease-in-out, background-color 0.15s ease-in-out",
                                                }}
                                            >
                                                {country.flagImageUrl ? (
                                                    <Box
                                                        component="img"
                                                        src={country.flagImageUrl}
                                                        alt={country.displayName}
                                                        sx={{
                                                            width: 20,
                                                            height: 14,
                                                            borderRadius: "3px",
                                                            objectFit: "cover",
                                                            border: "1px solid rgba(255,255,255,0.1)",
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                ) : (
                                                    <Box sx={{ width: 20, height: 14, borderRadius: "3px", backgroundColor: "rgba(255,255,255,0.1)", flexShrink: 0 }} />
                                                )}
                                                <Typography
                                                    sx={{
                                                        color: "#fff",
                                                        fontSize: "0.69rem",
                                                        fontWeight: 500,
                                                        flex: 1,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}
                                                >
                                                    {country.displayName}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        width: 13,
                                                        height: 13,
                                                        borderRadius: "3px",
                                                        border: "1px solid rgba(255,255,255,0.2)",
                                                        backgroundColor: isSelected ? "rgba(232, 163, 61, 0.9)" : "rgba(255,255,255,0.04)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {isSelected && <CheckIcon sx={{ fontSize: "0.65rem", color: "#fff" }} />}
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}
                    </Box>

                </div>
            </div>
            <div className={classes.floatingQueueEstimate}>
                <Typography className={classes.floatingQueueTitle}>
                    Queue Fit Estimate
                </Typography>
                <QueueEstimateIndicator estimate={queueEstimate} compact />
            </div>
        </div>
    );
}